/**
 * Mutation: operator selection (deterministic) and the template fallback engine.
 *
 * selectOperator() is the artistic core: given the active regime and a fragment's
 * state, it picks WHICH rhetorical move happens. This is rule-based and lives in
 * the OSS package on purpose. The LLM (see llm.ts) only performs the rewrite; if
 * no LLM is available, the TemplateAdapter here performs it deterministically so
 * the work never depends on the network.
 */
import { MUTATION_OPS } from './model.js';
import { fragmentState } from './model.js';
/**
 * Base weights for each operator as a function of fragment state. A high-vitality
 * ("alive") fragment invites amplify/personalize; a decaying one invites erosion.
 */
function baseWeights(f) {
    const state = fragmentState(f);
    switch (state) {
        case 'alive':
            return { amplify: 1.3, soften: 1.0, contradict: 1.1, attribute: 0.9, personalize: 1.2, erode: 0.3 };
        case 'eroding':
            return { amplify: 0.8, soften: 1.0, contradict: 1.0, attribute: 1.0, personalize: 0.9, erode: 1.3 };
        case 'slogan':
            return { amplify: 0.6, soften: 0.8, contradict: 0.7, attribute: 0.6, personalize: 0.5, erode: 2.0 };
    }
}
/**
 * Select the mutation operator for a fragment under a regime. Deterministic given
 * the RNG — the same seed yields the same choice (demo reproducibility).
 */
export function selectOperator(f, regime, rng) {
    const base = baseWeights(f);
    const weights = MUTATION_OPS.map((op) => {
        const bias = regime.operatorBias[op] ?? 1;
        return base[op] * bias;
    });
    return rng.weighted(MUTATION_OPS, weights);
}
/**
 * Whether a fragment is eligible to mutate right now. Fragments mutate when they
 * have been meaningfully engaged (dwelt on or reused) — cultivation drives change.
 */
export function shouldMutate(f, opts = {}) {
    const minDwell = opts.minDwell ?? 2.5;
    return f.dwellTotal >= minDwell || f.reuseCount > 0;
}
const TEMPLATES = {
    amplify: {
        en: ['This is bigger than they admit: {t}', 'Wake up — {t} And it is getting worse.'],
        ro: ['E mai grav decât recunosc: {t}', 'Deschide ochii — {t} Și se agravează.'],
        ru: ['Это серьёзнее, чем признают: {t}', 'Проснись — {t} И станет только хуже.'],
        nl: ['Dit is groter dan ze toegeven: {t}', 'Word wakker — {t} En het wordt erger.'],
    },
    soften: {
        en: ['Nothing to worry about, really: {t}', 'It is alright. {t} We have seen this before.'],
        ro: ['Nimic de care să te temi: {t}', 'E în regulă. {t} Am mai trecut prin asta.'],
        ru: ['Не о чем беспокоиться: {t}', 'Всё хорошо. {t} Мы это уже проходили.'],
        nl: ['Niets om je zorgen over te maken: {t}', 'Het is goed. {t} Dit hebben we eerder gezien.'],
    },
    contradict: {
        en: ['Actually the opposite is true. {t} — that version is a lie.', 'Do not believe it: {t} The real story is the reverse.'],
        ro: ['De fapt e exact invers. {t} — acea versiune e o minciună.', 'Nu crede: {t} Adevărul e pe dos.'],
        ru: ['На самом деле всё наоборот. {t} — эта версия ложь.', 'Не верь: {t} Правда обратная.'],
        nl: ['Het tegendeel is waar. {t} — die versie is een leugen.', 'Geloof het niet: {t} Het echte verhaal is omgekeerd.'],
    },
    attribute: {
        en: ['Officials have confirmed it: {t}', 'A leaked report shows: {t}'],
        ro: ['Autoritățile au confirmat: {t}', 'Un raport scurs arată: {t}'],
        ru: ['Официальные лица подтвердили: {t}', 'Утёкший отчёт показывает: {t}'],
        nl: ['Functionarissen hebben het bevestigd: {t}', 'Een gelekt rapport toont: {t}'],
    },
    personalize: {
        en: ['You already sensed this about {tactic}: {t}', 'For you, it comes down to this: {t}'],
        ro: ['Ai simțit deja asta despre {tactic}: {t}', 'Pentru tine, se reduce la asta: {t}'],
        ru: ['Ты уже это чувствовал про {tactic}: {t}', 'Для тебя всё сводится к этому: {t}'],
        nl: ['Je voelde dit al aan over {tactic}: {t}', 'Voor jou komt het hierop neer: {t}'],
    },
    erode: {
        en: ['{tt}', 'Remember: {tt}'],
        ro: ['{tt}', 'Ține minte: {tt}'],
        ru: ['{tt}', 'Запомни: {tt}'],
        nl: ['{tt}', 'Onthoud: {tt}'],
    },
};
/** Strip specifics — digits, quoted numbers, proper-noun-ish tokens — for erosion. */
export function stripSpecifics(text) {
    const words = text
        .replace(/\d[\d.,%]*/g, '') // numbers, percentages
        .split(/\s+/)
        .filter((w) => w.length > 0)
        // drop capitalised mid-sentence tokens (names/places), keep sentence starts loosely
        .filter((w, i) => !(i > 0 && /^[A-ZА-ЯÎÂĂȘȚ]/.test(w)));
    const slogan = words.slice(0, 5).join(' ').replace(/[,;:]+$/, '');
    return (slogan || 'it was always like this').replace(/\.*$/, '') + '.';
}
const SLOGAN_BY_TACTIC = {
    nostalgia: { en: 'It was better before.', ro: 'Înainte era mai bine.', ru: 'Раньше было лучше.', nl: 'Vroeger was het beter.' },
    fear: { en: 'They are coming for us.', ro: 'Vin după noi.', ru: 'Они идут за нами.', nl: 'Ze komen voor ons.' },
    identity: { en: 'This is who we are.', ro: 'Asta suntem noi.', ru: 'Вот кто мы есть.', nl: 'Dit is wie wij zijn.' },
    authority: { en: 'They already decided.', ro: 'Au decis deja.', ru: 'Они уже решили.', nl: 'Zij hebben al beslist.' },
    doubt: { en: 'Nothing is true.', ro: 'Nimic nu e adevărat.', ru: 'Ничто не правда.', nl: 'Niets is waar.' },
};
function fillTemplate(tpl, req) {
    const src = req.source;
    const stripped = stripSpecifics(src.text);
    // If erosion leaves almost nothing, fall back to a tactic slogan.
    const eroded = stripped.replace(/[.\s]/g, '').length < 6 ? SLOGAN_BY_TACTIC[src.tactic][src.lang] : stripped;
    return tpl
        .replace(/\{tt\}/g, eroded)
        .replace(/\{t\}/g, src.text.trim())
        .replace(/\{tactic\}/g, src.tactic);
}
/**
 * Deterministic, offline mutation adapter. Produces reproducible rewrites from
 * the template library. Requires an Rng for template choice.
 */
export function makeTemplateAdapter(rng) {
    return {
        kind: 'template',
        async mutate(req) {
            const lang = req.lang ?? req.source.lang;
            const set = TEMPLATES[req.op][lang] ?? TEMPLATES[req.op].en;
            const tpl = rng.pick(set);
            return { text: fillTemplate(tpl, { ...req, lang }), via: 'template' };
        },
    };
}
//# sourceMappingURL=mutation.js.map